import { useEditor, EditorContent, Editor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react'
import { Code, ImagePlus, Loader2 } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { VariableExtension } from './VariableExtension'
import { MacroImageExtension } from './MacroImageExtension'
import BubbleMenuToolbar from './BubbleMenuToolbar'
import VariablePicker from './VariablePicker'
import { Variable } from '@/lib/variables'
import { isValidImage } from '@/lib/imageUpload'

interface MacroEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  onImageUpload?: (file: File) => Promise<string>
}

export interface MacroEditorRef {
  editor: Editor | null
}

const MacroEditor = forwardRef<MacroEditorRef, MacroEditorProps>(
  ({ value, onChange, placeholder = 'Escreva o conteúdo do macro...', onImageUpload }, ref) => {
    const [showVariablePicker, setShowVariablePicker] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const initialValueRef = useRef(value)

    const handleImageUpload = useCallback(
      async (file: File): Promise<string> => {
        if (onImageUpload) {
          return onImageUpload(file)
        }

        // Default: return a data URL for preview
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
      },
      [onImageUpload]
    )

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2] },
          link: false,
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
        VariableExtension,
        MacroImageExtension.configure({
          onUpload: handleImageUpload,
          onUploadStart: () => setIsUploading(true),
          onUploadEnd: () => setIsUploading(false),
          onUploadError: (error) => {
            console.error('Image upload failed:', error)
            setIsUploading(false)
          },
        }),
      ],
      content: value || '',
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()
        onChange(html)
      },
      editorProps: {
        attributes: {
          class: 'prose prose-sm max-w-none prose-p:my-0 prose-p:leading-normal prose-img:my-0 focus:outline-none p-4 min-h-[200px]',
        },
      },
    })

    useImperativeHandle(ref, () => ({ editor }))

    // Update editor content when value changes externally (only on initial load)
    useEffect(() => {
      if (editor && value !== initialValueRef.current && !editor.isFocused) {
        const currentHtml = editor.getHTML()
        if (currentHtml !== value) {
          editor.commands.setContent(value || '')
          initialValueRef.current = value
        }
      }
    }, [value, editor])

    const handleInsertVariable = useCallback(
      (variable: Variable) => {
        if (editor) {
          editor.commands.insertVariable({
            key: variable.key,
            label: variable.label,
            fallback: null,
          })
          editor.commands.focus()
        }
        setShowVariablePicker(false)
      },
      [editor]
    )

    const handleFileSelect = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (!files || !editor) return

        Array.from(files).forEach((file) => {
          if (isValidImage(file)) {
            editor.commands.uploadImage(file)
          }
        })

        // Reset input
        e.target.value = ''
      },
      [editor]
    )

    return (
      <div className="relative">
        <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white overflow-hidden">
          {/* Editor Content */}
          <EditorContent
            editor={editor}
            className="[&_.ProseMirror]:min-h-[200px] [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mb-2 [&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mb-2 [&_.ProseMirror_code]:bg-gray-100 [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm [&_.ProseMirror_pre]:bg-gray-900 [&_.ProseMirror_pre]:text-gray-100 [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:overflow-x-auto [&_.ProseMirror_a]:text-blue-600 [&_.ProseMirror_a]:underline [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-2 [&_.ProseMirror_img.ProseMirror-selectednode]:ring-2 [&_.ProseMirror_img.ProseMirror-selectednode]:ring-blue-500 [&_.ProseMirror_img.ProseMirror-selectednode]:ring-offset-2"
          />

          {/* Bubble Menu */}
          {editor && (
            <BubbleMenu
              editor={editor}
              shouldShow={({ editor: bubbleEditor, state }) => {
                // Don't show for images or when selection is empty
                const { from, to } = state.selection
                const text = state.doc.textBetween(from, to, '')
                return text.length > 0 && !bubbleEditor.isActive('image')
              }}
            >
              <BubbleMenuToolbar editor={editor} />
            </BubbleMenu>
          )}

          {/* Bottom Toolbar - Always visible */}
          <div className="border-t border-gray-200 px-3 py-2 flex items-center gap-2">
              {/* Variable Button */}
              <Popover open={showVariablePicker} onOpenChange={setShowVariablePicker}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-variable-trigger
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <Code className="w-4 h-4" />
                    Inserir variável
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-80">
                  <VariablePicker onSelect={handleInsertVariable} onClose={() => setShowVariablePicker(false)} />
                </PopoverContent>
              </Popover>

              {/* Image Button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                {isUploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ImagePlus className="w-4 h-4" />
                )}
                Inserir imagem
              </button>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
        </div>

      </div>
    )
  }
)

MacroEditor.displayName = 'MacroEditor'
export default MacroEditor
